import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import OtherAmountModal from './OtherAmountModal';
import { CheckoutWizardProps } from '../CheckoutWizardInterfaces';
import {
    AmountCapsuleGrid,
    AmountCapsuleGridProps,
} from '../../Capsules/AmountCapsuleGrid';
import { TABLET_WIDTH } from '../../../utils';
import { usePushEvents } from '../../../../../hooks/PushEvents';
import {
    amountSelected,
    giftAmountScreen,
    numberPadOkSelected,
    otherAmountSelected,
} from '../../../../../hooks/PushEvents/clevertapEvents';
import { setQueryString } from '../../../../../hooks/useQueryString';
import { Text } from '../../common/Text/Text';
import FooterBanner, {
    getFooterBannerVisibility,
} from '../../../../Footer/FooterBanner';
import { webConfig } from '../../../../../utils/webConfig';
import { FeatureFlag } from '../../../../../constants';
import { useFeatureFlag } from '../../../../../providers';
import MetaTags from '../../../../MetaTags';
import { Donee, useDonorProfile, useGiftSummary } from '../../../../../api';

const isDoneeInactive = (donee: Donee) => {
    return !donee.organization.canReceiveDonations;
};

const StyledDiv = styled.div`
    max-width: 546px;
    margin-left: auto;
    margin-right: auto;
    .text {
        text-align: center;
        margin-top: 24px;
        margin-bottom: 66px;
    }
    .sub-text {
        margin-top: 24px;
        margin-bottom: 16px;
    }
    @media only screen and (max-width: ${TABLET_WIDTH}) {
        .text {
            margin-top: 0px;
            margin-bottom: 20px;
        }
    }
`;

type AmountWizardProps = CheckoutWizardProps;

const AmountWizard: React.FC<AmountWizardProps> = ({
    donee,
    setRoute,
    addAmount,
}) => {
    const { donorProfile } = useDonorProfile();
    const { totalGiftsCount } = useGiftSummary();
    const { pushEvent } = usePushEvents();
    const { isFeatureEnabled } = useFeatureFlag();
    const [otherModal, setOtherModal] = useState<boolean>(false);

    const showFooterBanner = useMemo(
        () =>
            getFooterBannerVisibility(
                totalGiftsCount,
                isFeatureEnabled,
                Boolean(donorProfile),
            ),
        [totalGiftsCount, isFeatureEnabled, donorProfile],
    );

    const bannerControlValue = useMemo(
        () =>
            isFeatureEnabled(FeatureFlag.OGA_APPGIFTAMOUNTBANNER_SIGNEDOUT_V2)
                ? 'Variation'
                : 'Control',
        [isFeatureEnabled],
    );

    useEffect(() => {
        pushEvent(giftAmountScreen, {
            loggedIn: Boolean(donorProfile),
            doneeId: donee.idShort,
            'oga-appgiftamountbanner-signedout-v2': bannerControlValue,
            'oga-information-banner': isFeatureEnabled(
                FeatureFlag.OGA_INFORMATION_BANNER,
            )
                ? 'Variation'
                : 'Control',
            'oga-appfooterdeeplinks': isFeatureEnabled(
                FeatureFlag.OGA_APPFOOTERDEEPLINKS,
            )
                ? 'Variation'
                : 'Control',
        });
        // run only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick: AmountCapsuleGridProps['onClick'] = (
        amount: number | 'Other',
    ) => {
        if (amount === 'Other') {
            setOtherModal(true);
            pushEvent(otherAmountSelected, {
                loggedIn: Boolean(donorProfile),
                doneeId: donee.idShort,
            });
            return;
        }
        pushEvent(amountSelected, {
            amount,
            loggedIn: Boolean(donorProfile),
            doneeId: donee.idShort,
        });
        setQueryString('amount', amount);
        setRoute('envelopes');
        addAmount(amount);
    };

    const handleOtherAmount = (amount: number) => {
        handleClick(amount);
        pushEvent(numberPadOkSelected, {
            amount,
            loggedIn: Boolean(donorProfile),
            doneeId: donee.idShort,
        });
    };

    if (
        webConfig.isBlockedDonee(donee.idShort) ||
        (isDoneeInactive(donee) &&
            isFeatureEnabled(FeatureFlag.OGA_DEACTIVATE_UNREGISTERED_GPS))
    ) {
        return (
            <>
                <MetaTags title="Unknown" />
                <StyledDiv>
                    <Text
                        className={'text'}
                        variant={'large'}
                        weight={'extraBold'}
                        color={'primarySecondaryContent'}
                    >
                        This organization is not registered
                    </Text>
                    <Text className={'sub-text'}>
                        Please go to{' '}
                        <a href={webConfig.mainSearchSite}>Givelify.com</a> to
                        find another organization.
                    </Text>
                </StyledDiv>
                {showFooterBanner && (
                    <FooterBanner bannerControlValue={bannerControlValue} />
                )}
            </>
        );
    }

    return (
        <>
            <StyledDiv>
                <Text
                    className={'text'}
                    variant={'large'}
                    weight={'extraBold'}
                    color={'primarySecondaryContent'}
                >
                    Choose a gift amount
                </Text>
                <AmountCapsuleGrid
                    amounts={[...donee.offeringAmounts, 'Other']}
                    onClick={handleClick}
                />
            </StyledDiv>
            <OtherAmountModal
                open={otherModal}
                onSubmit={handleOtherAmount}
                onClose={() => setOtherModal(false)}
            />
            {showFooterBanner && (
                <FooterBanner bannerControlValue={bannerControlValue} />
            )}
        </>
    );
};

export default AmountWizard;
